import React, { useState, useEffect } from 'react';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatIcon from '@mui/icons-material/Chat';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { supabase } from '../supabaseClient';

export default function ChatwootIntegrationsList({ onSelectIntegration, onCreateNew, onClose }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setError('Not authenticated');
        return;
      }
      
      const { data, error: rpcError } = await supabase
        .rpc('get_user_chatwoot_integrations_summary', { p_user_id: userId });
      
      if (rpcError) {
        console.error('RPC error loading integrations:', rpcError);
        setError(rpcError.message);
        return;
      }
      
      setIntegrations(data || []);
    } catch (e) {
      console.error('Failed to load integrations:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (integrationId, integrationName, currentState) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      
      const { error } = await supabase
        .from('chatwoot_configurations')
        .update({ is_active: !currentState, updated_at: new Date().toISOString() })
        .eq('id', integrationId)
        .eq('rag_user_id', userId);
      
      if (error) {
        console.error('Failed to toggle integration:', error);
        setError(error.message);
        return;
      }
      
      // Reload list to reflect changes
      await loadIntegrations();
    } catch (e) {
      console.error('Failed to toggle integration:', e);
      setError(e.message);
    }
  };

  const handleDelete = async (integrationId, integrationName) => {
    const confirmMsg = `Are you sure you want to delete integration "${integrationName}"?\n\nThis will:\n- Remove the integration configuration\n- Remove all file mappings for this integration\n- NOT delete files themselves\n\nThis cannot be undone.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      
      const { error } = await supabase
        .from('chatwoot_configurations')
        .delete()
        .eq('id', integrationId)
        .eq('rag_user_id', userId);
      
      if (error) {
        console.error('Failed to delete integration:', error);
        setError(error.message);
        return;
      }
      
      // Reload list
      await loadIntegrations();
    } catch (e) {
      console.error('Failed to delete integration:', e);
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mx: 2, my: 1 }}>
        {error}
      </Alert>
    );
  }

  if (integrations.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          No integrations yet
        </Typography>
        <ListItemButton sx={{ pl: 11 }} onClick={() => { if (onCreateNew) onCreateNew(); if (onClose) onClose(); }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Create New Integration" />
        </ListItemButton>
      </Box>
    );
  }

  return (
    <List dense>
      {integrations.map(int => (
        <Box key={int.integration_id} sx={{ display: 'flex', alignItems: 'center', pl: 11 }}>
          <ListItemButton 
            sx={{ flexGrow: 1, pr: 0 }}
            onClick={() => {
              if (onSelectIntegration) onSelectIntegration(int.integration_id);
              if (onClose) onClose();
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ChatIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText 
              primary={int.integration_name}
              secondary={`${int.file_count || 0} files`}
              primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
            {!int.is_active && (
              <Chip label="Inactive" size="small" color="default" sx={{ mr: 0.5 }} />
            )}
          </ListItemButton>
          
          <Tooltip title={int.is_active ? 'Deactivate' : 'Activate'}>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleToggleActive(int.integration_id, int.integration_name, int.is_active);
              }}
            >
              {int.is_active ? (
                <ToggleOnIcon fontSize="small" color="success" />
              ) : (
                <ToggleOffIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Delete">
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(int.integration_id, int.integration_name);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      
      <ListItemButton sx={{ pl: 11 }} onClick={() => { if (onCreateNew) onCreateNew(); if (onClose) onClose(); }}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <AddIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary="Create New Integration" 
          primaryTypographyProps={{ fontSize: '0.875rem' }}
        />
      </ListItemButton>
    </List>
  );
}




